/* eslint-disable react/prop-types */
import React, {
    useReducer, useEffect, useContext,
} from 'react';
import PropTypes from 'prop-types';
import Table from 'components/widgets/Table';
import {
    makeStyles, Dialog, DialogContent, Typography, AppBar, Toolbar, Badge, Button,
} from '@material-ui/core';
import ModalUtils from 'utils/ModalUtils';
import { useLazyQuery, useQuery } from '@apollo/client';
import clsx from 'clsx';
import AccountingStyles from 'styles/modules/accounting/AccountingStyles';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { FetchPolicy } from 'utils/enum/Core';
import PartsQuery from 'services/graphQL/query/parts/PartsQuery';
import InputSearch from 'components/widgets/InputSearch';
import {
    CheckBoxIcon, CheckBoxOutlineBlankIcon, ThumbUpAltOutlinedIcon,
} from 'components/icons';
import Header from 'components/widgets/Header';
import LabelValue from 'components/widgets/LabelValue';
import NumberUtils from 'lib/NumberUtils';
import Filter from 'components/widgets/Filter';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import UserContext from 'components/context/UserContext';
import ServiceQuery from 'services/graphQL/query/service/ServiceQuery';

const useStyle = makeStyles((theme) => AccountingStyles.checkActionList(theme));
const ownStyle = makeStyles((theme) => (
    {
        dialogContent: {
            height: '70vh',
            '& > div:first-child': {
                height: '70vh',
            },
            padding: 0,
            overflow: 'hidden',
        },
        mainTable: {
            overflow: 'auto',
            flex: 1,
        },
        filterWidth: {
            width: '170px',
            border: `solid 1px ${theme.palette.border.ghost}`,
            padding: '4px 5px',
            borderRadius: '0.2rem',
            paddingRight: '10px',
            '& div': {
                fontSize: '14px',
                fontWeight: 400,
            },
        },
        iconFilterCls: {
            position: 'relative',
            right: '25px',
            top: '6px',
            zIndex: '-1',
        },
        paddingRight10: {
            paddingRight: '10px',
        },
        paddingTop10: {
            paddingTop: '10px',
        },
        labelFont12: {
            fontSize: '12px',
        },
        c: {
            margin: '5px 0px',
        },
        paddingLegend: {
            padding: '10px 10px 0px 10px',
        },
    }
));

const ACTION_TYPES = {
    SET_TABLE: 'setTable',
    SET_INITIAL_STATE: 'setInitialState',
    SET_STATE_VALUES: 'setStateValue',
    LOAD_MORE: 'setLoadMore',
    SET_PARAMS: 'setParams',
    SET_TABLE_APPROVED: 'setTableApproved',
    SET_SERVICE_SETTINGS: 'setServiceSettings',
};

const partsReducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPES.SET_TABLE: {
        return {
            ...state,
            table: action.value,
        };
    }
    case ACTION_TYPES.SET_PARAMS: {
        return {
            ...state,
            table: {
                records: [],
                totalCount: 0,
            },
            params: action.value,
        };
    }
    case ACTION_TYPES.SET_TABLE_APPROVED: {
        const newRecords = state.table.records.map((item) => {
            const isChecked = item.partsInventoryId === action.value.partsInventoryId && item.ticketNumber === action.value.ticketNumber;
            return ({
                ...item,
                isChecked: isChecked ? !item.isChecked : item.isChecked,
            });
        });

        return {
            ...state,
            table: {
                totalCount: state.table.totalCount,
                records: newRecords,
            },
            isDirty: newRecords.filter((c) => c.isChecked).length > 0,
        };
    }
    case ACTION_TYPES.LOAD_MORE: {
        return {
            ...state,
            params: action.value,
        };
    }
    case ACTION_TYPES.SET_INITIAL_STATE: {
        return action.value;
    }
    case ACTION_TYPES.SET_STATE_VALUES: {
        return { ...state, ...action.value };
    }
    case ACTION_TYPES.SET_SERVICE_SETTINGS: {
        return {
            ...state,
            serviceSettings: {
                ...state.serviceSettings,
                ...action.payload,
            },
        };
    }
    default:
        return state;
    }
};

const PartsToReturn = (props) => {
    const classes = {
        ...useStyle(),
        ...ownStyle(),
    };

    const {
        open, onPopupClose, onSelectItems,
        lotName: lotNameProcess, customerCode,
    } = props;

    const initialState = {
        params: {
            paginate: {
                init: 0,
                limit: 100,
            },
            search: '',
            lotSelected: [],
        },
        table: {
            records: [],
            totalCount: 0,
        },
        isDirty: false,
    };

    const [state, dispatch] = useReducer(partsReducer, initialState);
    const { userInformation = {} } = useContext(UserContext);
    const availableLots = (userInformation.lots || []).map((item) => ({ value: item.lotId, label: item.lotName }));

    const {
        params, table, isDirty,
    } = state;

    const {
        data, loading, error,
    } = useQuery(PartsQuery.GET_PARTS_TICKET_BY_CUSTOMER, {
        variables: {
            paginate: params.paginate,
            filter: {
                search: params.search,
            },
            customerCode,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [getServiceSettingsKeys] = useLazyQuery(ServiceQuery.GET_SERVICE_DEFAULT_SETTINGS, {
        onCompleted: (res) => {
            if (res.getServiceSettingsKeys) {
                dispatch({
                    type: ACTION_TYPES.SET_SERVICE_SETTINGS,
                    payload: res.getServiceSettingsKeys,
                });
            }
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const loadMore = () => {
        const currentOffset = table?.records?.length || 0;
        const { paginate } = params;

        dispatch({
            type: ACTION_TYPES.LOAD_MORE,
            value: {
                ...params,
                paginate: {
                    ...paginate,
                    init: currentOffset,
                },
            },
        });
    };

    const onSearch = (text) => {
        dispatch({
            type: ACTION_TYPES.SET_PARAMS,
            value: {
                ...params,
                search: text.trim(),
                paginate: {
                    ...params.paginate,
                    init: 0,
                },
            },
        });
    };

    const onSelectedItems = () => {
        const selectedRecords = table?.records.filter((c) => c.isChecked).map((item) => {
            const { partsInventory } = item;
            return ({ ...item, ...partsInventory });
        });
        onSelectItems(selectedRecords);
    };

    const onLotChange = (lots) => {
        dispatch({
            type: ACTION_TYPES.SET_PARAMS,
            value: {
                ...params,
                lotSelected: lots,
                paginate: {
                    ...params.paginate,
                    init: 0,
                },
            },
        });
    };

    const {
        highlightedColor, columnHeaderStyle, columnCenter, columnStyle, columnLeft, columnRight,
        appBar, centerItems, title, dialogContent, main, containerFilterSearch, search,
        buttonSave, filterLabel, filterWidth, iconFilterCls, mainTable, paddingRight10,
    } = classes;

    const columns = [
        {

            Cell: (cell) => {
                const {
                    original: {
                        partsInventoryId, isChecked, quantityAvailableToReturn, ticketNumber,
                    },
                } = cell;

                if (quantityAvailableToReturn === 0) return null;

                return (
                    isChecked ? (
                        <CheckBoxIcon
                            className={highlightedColor}
                            onClick={() => dispatch({ type: ACTION_TYPES.SET_TABLE_APPROVED, value: { partsInventoryId, ticketNumber } })}
                        />
                    )
                        : (
                            <CheckBoxOutlineBlankIcon
                                className={highlightedColor}
                                onClick={() => dispatch({ type: ACTION_TYPES.SET_TABLE_APPROVED, value: { partsInventoryId, ticketNumber } })}
                            />
                        )
                );
            },
            width: 60,
            headerClassName: clsx(columnHeaderStyle, columnCenter),
            className: clsx(columnStyle, columnCenter),
            sortable: false,
            disableSortBy: false,
            filterable: false,
        },
        {
            minWidth: 30,
            width: 30,
            headerClassName: clsx(columnHeaderStyle, columnLeft),
            className: clsx(columnStyle, columnLeft),
            Cell: (cell) => cell.viewIndex + 1,
        },
        {
            Header: 'Ticket Number',
            minWidth: 140,
            width: 140,
            id: 'ticketNumber',
            accessor: 'ticketNumber',
            headerClassName: clsx(columnHeaderStyle, columnLeft),
            className: clsx(columnStyle, columnLeft),
        },
        {
            Header: 'Available to Return',
            minWidth: 160,
            width: 160,
            id: 'quantityAvailableToReturn',
            accessor: 'quantityAvailableToReturn',
            headerClassName: clsx(columnHeaderStyle, columnLeft),
            className: clsx(columnStyle, columnLeft),
        },
        {
            Header: 'Stock Number',
            minWidth: 140,
            width: 140,
            id: 'PartStockNumber',
            accessor: (record) => record,
            headerClassName: clsx(columnHeaderStyle, columnLeft),
            className: clsx(columnStyle, columnLeft),
            Cell: ({ value }) => {
                const { partsInventory: { partStockNumber } } = value;

                return <span>{partStockNumber}</span>;
            },
        },
        {
            Header: 'UPC',
            minWidth: 90,
            width: 90,
            id: 'UniversalProductCode',
            accessor: 'universalProductCode',
            headerClassName: clsx(columnHeaderStyle, columnLeft),
            className: clsx(columnStyle, columnLeft),
        },
        {
            Header: 'Description',
            minWidth: 200,
            id: 'Description',
            accessor: 'description',
            headerClassName: clsx(columnHeaderStyle, columnLeft),
            className: clsx(columnStyle, columnLeft),
        },
        {
            Header: 'Cost',
            minWidth: 90,
            width: 90,
            id: 'PartCost',
            accessor: 'partCost',
            headerClassName: clsx(columnHeaderStyle, columnRight),
            className: clsx(columnStyle, columnRight),
            Cell: ({ value }) => NumberUtils.applyCurrencyFormat(value),
        },
        {
            Header: 'List Price',
            minWidth: 90,
            width: 90,
            id: 'NetPrice',
            accessor: 'netPrice',
            headerClassName: clsx(columnHeaderStyle, columnRight),
            className: clsx(columnStyle, columnRight),
            Cell: ({ value }) => NumberUtils.applyCurrencyFormat(value),
        },
        {
            Header: 'Discount',
            minWidth: 90,
            width: 90,
            id: 'Discount',
            accessor: 'discount',
            headerClassName: clsx(columnHeaderStyle, columnRight),
            className: clsx(columnStyle, columnRight),
            Cell: ({ value }) => NumberUtils.applyCurrencyFormat(value),
        },
        {
            Header: 'Total',
            minWidth: 90,
            width: 90,
            id: 'Total',
            accessor: 'total',
            headerClassName: clsx(columnHeaderStyle, columnRight),
            className: clsx(columnStyle, columnRight),
            Cell: ({ value }) => NumberUtils.applyCurrencyFormat(value),
        },
        {
            Header: 'Lot Name',
            minWidth: 100,
            width: 100,
            id: 'LotName',
            accessor: (record) => record,
            headerClassName: clsx(columnHeaderStyle, columnLeft),
            className: clsx(columnStyle, columnLeft),
            Cell: ({ value }) => {
                const { partsInventory: { lotName } } = value;

                return <span>{lotName}</span>;
            },
        },
    ];

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
            return;
        }

        if (!loading) {
            const { records } = table;
            const { getPartsTicketByCustomer } = data;

            records.push(...getPartsTicketByCustomer.data);

            dispatch({
                type: ACTION_TYPES.SET_TABLE,
                value: {
                    records,
                    totalCount: getPartsTicketByCustomer.totalCount,
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, error]);

    useEffect(() => {
        const currentLot = availableLots?.filter((c) => c.label.toLowerCase() === lotNameProcess.toLowerCase());
        dispatch({
            type: ACTION_TYPES.SET_PARAMS,
            value: {
                ...params,
                lotSelected: currentLot,
                paginate: {
                    ...params.paginate,
                    init: 0,
                },
            },
        });

        if (lotNameProcess !== '') {
            getServiceSettingsKeys({
                variables: {
                    lotName: lotNameProcess,
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lotNameProcess, availableLots.length]);

    const approvedRecords = table?.records.filter((c) => c.isChecked).map((c) => c.partsInventoryId);

    return (
        <>
            <Dialog
                open={open}
                onClose={onPopupClose}
                maxWidth="lg"
                fullWidth
                disableBackdropClick
                disableEscapeKeyDown
                scroll="paper"
            >
                <AppBar className={appBar}>
                    <Toolbar className={centerItems}>
                        <Typography variant="h6" className={title}>
                            Select parts
                        </Typography>
                        <div className={centerItems}>
                            <IconButton edge="start" color="inherit" onClick={onPopupClose}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                    </Toolbar>
                </AppBar>
                <DialogContent className={dialogContent}>
                    <div className={main}>
                        <Header>
                            <div className={containerFilterSearch}>
                                <InputSearch
                                    customClasses={clsx(search)}
                                    initialSearch={params.search || ''}
                                    onSearch={onSearch}
                                />
                                <div>
                                    <LabelValue
                                        label="Items"
                                        value={table.totalCount}
                                        className={paddingRight10}
                                    />
                                </div>
                                <div className={`d-flex-align-baseline-space-between ${paddingRight10}`}>
                                    <Typography
                                        variant="h5"
                                        className={filterLabel}
                                    >
                                        Lot: &nbsp;
                                    </Typography>
                                    <Filter
                                        showTooltip
                                        useInternalSearch
                                        emptyRecord="All"
                                        maxWidthLabel={150}
                                        className={filterWidth}
                                        records={availableLots || []}
                                        selectedValues={params.lotSelected}
                                        applyFilter={(values) => onLotChange(values)}
                                        onClearFilter={() => onLotChange([])}
                                    />
                                    <ArrowDropDownIcon className={iconFilterCls} />
                                </div>
                                <Badge badgeContent={approvedRecords?.length ?? 0} color="primary">
                                    <Button
                                        className={buttonSave}
                                        startIcon={<ThumbUpAltOutlinedIcon />}
                                        size="small"
                                        disabled={!isDirty}
                                        onClick={onSelectedItems}
                                    >
                                        Select
                                    </Button>
                                </Badge>
                            </div>
                        </Header>
                        <div className={mainTable}>
                            <Table
                                data={table?.records}
                                columns={columns}
                                cursor="default"
                                load={loading}
                                totalRecords={table?.totalCount}
                                loadMore={loadMore}
                                rowSelected
                                enableRowHover
                                className="-highlight"
                            />
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

PartsToReturn.propTypes = {
    open: PropTypes.bool,
    onPopupClose: PropTypes.func,
    onSelectItems: PropTypes.func,
    lotName: PropTypes.string,
    customerCode: PropTypes.number,
};

PartsToReturn.defaultProps = {
    open: false,
    customerCode: null,
    onPopupClose: () => {},
    onSelectItems: () => {},
    lotName: '',
};

export default PartsToReturn;
